import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Clock, Settings, Mail, Calendar, FileText, Bell, RefreshCw, FileCheck } from 'lucide-react'
import { Modal } from 'components/ui/Modal'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { Textarea } from 'components/ui/Textarea'
import { Select } from 'components/ui/Select'
import { StepAction, emailConfigApi, EmailTemplate } from 'lib/api'
import { useToast } from 'contexts/ToastContext'
import { DEFAULT_ACTION_TYPE, DEFAULT_TRIGGER_TYPE, DEFAULT_DELAY_HOURS, EMAIL_TEMPLATE_FIELD_KEY } from 'constants/hiringProcess'
import {
  filterEmailTemplatesForDisplay,
  getEmailTemplateDisplayLanguage,
  getEmailTemplateDisplayName,
} from 'lib/emailTemplates'

interface StepActionModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (action: StepAction) => void
  action: StepAction | null
  actionTypes?: Array<{
    value: string
    label: string
    icon: string
    description: string
  }>
  triggerTypes?: Array<{
    value: string
    label: string
    description: string
  }>
}

const ACTION_ICONS: Record<string, React.ComponentType<any>> = {
  email: Mail,
  send_email: Mail,
  calendar: Calendar,
  schedule_meeting: Calendar,
  notification: Bell,
  send_notification: Bell,
  document: FileCheck,
  generate_document: FileCheck,
  evaluation: FileText,
  approval: RefreshCw,
  integration: Settings,
  custom: Settings,
  update_status: RefreshCw,
  create_task: FileText,
}

// Email config will be dynamically generated with fetched templates
type ConfigField = {
  key: string
  label: string
  type: 'text' | 'number' | 'textarea' | 'select' | 'checkbox'
  required?: boolean
  placeholder?: string
  options?: string[]
  min?: number
  helper?: string
  transform?: 'array' | 'json' | 'number'
  templateOptions?: EmailTemplate[]
}

const getEmailConfigFields = (emailTemplates: EmailTemplate[]): ConfigField[] => [
  { 
    key: EMAIL_TEMPLATE_FIELD_KEY, 
    label: 'Email Template', 
    type: 'select', 
    required: true,
    templateOptions: emailTemplates 
  },
]

// Schedule meeting config fields
const MEETING_CONFIG_FIELDS: ConfigField[] = [
  {
    key: 'show_schedule_button',
    label: 'Show "Schedule Meeting" button on candidate cards',
    type: 'checkbox',
    helper: 'Enable this to display the Schedule Meeting button on candidate cards in the kanban board for this step'
  }
]

export function StepActionModal({ 
  isOpen, 
  onClose, 
  onSuccess, 
  action,
  actionTypes,
  triggerTypes
}: StepActionModalProps) {
  const { t, i18n } = useTranslation()
  const { addToast } = useToast()

  const displayLanguage = useMemo(
    () => getEmailTemplateDisplayLanguage(i18n.language),
    [i18n.language]
  )
  
  // CRITICAL: Ensure we always have arrays - no matter what
  const safeActionTypes: Array<{value: string, label: string, icon: string, description: string}> = 
    (() => {
        try {
        if (actionTypes && Array.isArray(actionTypes)) {
          return actionTypes as Array<{value: string, label: string, icon: string, description: string}>
        }
      } catch (e) {
        // Silently fall back to defaults
      }
      // Return default action types if none provided or invalid
      return [
        { value: 'email', label: 'Send Email', icon: '📧', description: 'Send automated email' },
        { value: 'calendar', label: 'Schedule Meeting', icon: '📅', description: 'Schedule calendar meeting' },
      ]
    })()
    
  const safeTriggerTypes: Array<{value: string, label: string, description: string}> = 
    (() => {
      try {
        if (triggerTypes && Array.isArray(triggerTypes)) {
          return triggerTypes as Array<{value: string, label: string, description: string}>
        }
      } catch (e) {
        // Silently fall back to defaults
      }
      // Return default trigger types if none provided or invalid
      return [
        { value: 'manual', label: 'Manual', description: 'Manually triggered by user' },
        { value: 'automatic', label: 'Automatic', description: 'Triggered automatically' },
        { value: 'on_enter', label: 'On Step Entry', description: 'Triggered when candidate enters this step' },
        { value: 'on_exit', label: 'On Step Exit', description: 'Triggered when candidate exits this step' },
        { value: 'on_completion', label: 'On Step Completion', description: 'Triggered when step is completed' },
        { value: 'scheduled', label: 'Scheduled', description: 'Triggered at a specific time' },
      ]
    })()
  
  // Filter trigger types based on action type
  const getFilteredTriggerTypes = () => {
    if (actionForm.action_type === 'email') {
      return safeTriggerTypes.filter(
        t => t.value === 'automatic' || t.value === 'manual' || t.value === 'on_enter'
      )
    }
    return safeTriggerTypes
  }
  
  // Action form state
  const [actionForm, setActionForm] = useState({
    name: '', // Auto-generated from action type and trigger
    description: '',
    action_type: DEFAULT_ACTION_TYPE,
    trigger_type: DEFAULT_TRIGGER_TYPE,
    is_active: true,
    is_required: false,
    delay_hours: DEFAULT_DELAY_HOURS,
    expires_hours: undefined as number | undefined,
    configuration: {} as Record<string, any>
  })

  // Email templates state
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([])
  const [loadingTemplates, setLoadingTemplates] = useState(false)

  const loadEmailTemplates = React.useCallback(async () => {
    setLoadingTemplates(true)
    try {
      const templates = await emailConfigApi.getAllTemplates({ language: displayLanguage })
      setEmailTemplates(
        filterEmailTemplatesForDisplay(
          templates.filter((template: EmailTemplate) => template.is_active),
          displayLanguage
        )
      )
    } catch (error) {
      setEmailTemplates([])
    } finally {
      setLoadingTemplates(false)
    }
  }, [displayLanguage])

  // Fetch email templates when modal opens and action type is email
  useEffect(() => {
    if (isOpen && actionForm.action_type === 'email') {
      loadEmailTemplates()
    }
  }, [isOpen, actionForm.action_type, loadEmailTemplates])

  useEffect(() => {
    if (action) {
      setActionForm({
        name: action.name,
        description: action.description || '',
        action_type: action.action_type,
        trigger_type: action.trigger_type,
        is_active: action.is_active,
        is_required: action.is_required,
        delay_hours: action.delay_hours || 0,
        expires_hours: action.expires_hours,
        configuration: action.configuration || {}
      })
      // Load templates if action is email type
      if (action.action_type === 'email' && isOpen) {
        loadEmailTemplates()
      }
    } else {
      // Reset form for new action
      setActionForm({
        name: '',
        description: '',
        action_type: DEFAULT_ACTION_TYPE,
        trigger_type: DEFAULT_TRIGGER_TYPE,
        is_active: true,
        is_required: false,
        delay_hours: DEFAULT_DELAY_HOURS,
        expires_hours: undefined,
        configuration: {}
      })
      // Load templates for new email action
      if (isOpen) {
        loadEmailTemplates()
      }
    }
  }, [action, isOpen, loadEmailTemplates])

  // Reset trigger_type to 'manual' if action_type changes to email and current trigger_type is not valid
  useEffect(() => {
    if (actionForm.action_type === 'email') {
      const validTriggers = ['automatic', 'manual', 'on_enter']
      if (!validTriggers.includes(actionForm.trigger_type)) {
        setActionForm(prev => ({ ...prev, trigger_type: 'manual' }))
      }
    }
  }, [actionForm.action_type])

  const normalizeConfiguration = React.useCallback(
    (configuration: Record<string, any>, fields: ConfigField[]) => {
      const normalized: Record<string, any> = { ...configuration }

      for (const field of fields) {
        if (!(field.key in normalized)) continue

        let value = normalized[field.key]

        if (typeof value === 'string') {
          value = value.trim()
        }

        switch (field.transform) {
          case 'array':
            if (typeof value === 'string') {
              value = value
                .split(/[\n,]/)
                .map((item) => item.trim())
                .filter(Boolean)
            } else if (Array.isArray(value)) {
              value = value
                .map((item) => (typeof item === 'string' ? item.trim() : item))
                .filter(Boolean)
            }
            break
          case 'json':
            if (typeof value === 'string' && value.length > 0) {
              try {
                value = JSON.parse(value)
              } catch (error) {
                addToast({
                  type: 'error',
                  title: t('common.error'),
                  message: t('hiringProcess.errors.invalidJson', 'Payload must be valid JSON'),
                })
                return null
              }
            }
            break
          case 'number':
            if (typeof value === 'string' && value !== '') {
              const parsed = Number(value)
              value = Number.isNaN(parsed) ? undefined : parsed
            }
            break
          default:
            break
        }

        normalized[field.key] = value
      }

      Object.keys(normalized).forEach((key) => {
        const value = normalized[key]
        const shouldRemove =
          value === undefined ||
          value === null ||
          (typeof value === 'string' && value.trim() === '') ||
          (Array.isArray(value) && value.length === 0)

        if (shouldRemove) {
          delete normalized[key]
        }
      })

      return normalized
    },
    [addToast, t]
  )

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    // For calendar actions, set default trigger type if not set (field is hidden)
    const isCalendarAction = actionForm.action_type === 'calendar' || actionForm.action_type === 'schedule_meeting'
    if (isCalendarAction && !actionForm.trigger_type) {
      actionForm.trigger_type = 'manual'
    }
    
    // Validate required fields (skip trigger_type for calendar actions since it's hidden)
    if (!isCalendarAction && !actionForm.trigger_type) {
      addToast({ type: 'error', title: t('common.error'), message: t('hiringProcess.errors.triggerTypeRequired') })
      return
    }

    const configurationFields = getConfigurationFields(actionForm.action_type)
    const normalizedConfiguration = normalizeConfiguration(actionForm.configuration, configurationFields || [])
    if (normalizedConfiguration === null) {
      return
    }

    // Validate email template if action type is email
    if ((actionForm.action_type === 'email' || actionForm.action_type === 'send_email') && 
        (!normalizedConfiguration[EMAIL_TEMPLATE_FIELD_KEY] && normalizedConfiguration[EMAIL_TEMPLATE_FIELD_KEY] !== 0)) {
      addToast({ type: 'error', title: t('common.error'), message: t('hiringProcess.errors.emailTemplateRequired') })
      return
    }

    if (configurationFields) {
      for (const field of configurationFields) {
        if (!field.required) continue
        const value = normalizedConfiguration[field.key]
        const isEmpty =
          value === undefined ||
          value === null ||
          (typeof value === 'string' && value.trim() === '') ||
          (Array.isArray(value) && value.length === 0)

        if (isEmpty) {
          addToast({
            type: 'error',
            title: t('common.error'),
            message: `${field.label} ${t('hiringProcess.errors.fieldRequiredSuffix', 'is required')}`,
          })
          return
        }
      }
    }
    
    // Auto-generate name if empty
    const actionName = actionForm.name.trim() || (() => {
      const actionTypeLabel = safeActionTypes.find(t => t.value === actionForm.action_type)?.label || actionForm.action_type
      // For calendar actions, don't include trigger type in name since it's hidden
      if (isCalendarAction) {
        return actionTypeLabel
      }
      const triggerLabel = safeTriggerTypes.find(t => t.value === actionForm.trigger_type)?.label || actionForm.trigger_type
      return `${actionTypeLabel} - ${triggerLabel}`
    })()

    // Normalize action_type to match backend (email, calendar, notification, document)
    let normalizedActionType = actionForm.action_type
    const actionTypeMapping: Record<string, string> = {
      'send_email': 'email',
      'schedule_meeting': 'calendar',
      'send_notification': 'notification',
      'generate_document': 'document',
    }
    if (actionTypeMapping[actionForm.action_type]) {
      normalizedActionType = actionTypeMapping[actionForm.action_type]
    }

    const actionData: StepAction = {
      ...action,
      ...actionForm,
      name: actionName,
      action_type: normalizedActionType,
      configuration: normalizedConfiguration,
      step: action?.step || 0,
      order: action?.order || 0,
      id: action?.id || Date.now(),
      created_at: action?.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
      created_by: action?.created_by
    }

    onSuccess(actionData)
  }

  const updateConfiguration = (key: string, value: any) => {
    setActionForm((prev) => {
      const nextConfiguration = { ...prev.configuration }

      const shouldUnset =
        value === undefined ||
        value === null ||
        (typeof value === 'string' && value === '')

      if (shouldUnset) {
        delete nextConfiguration[key]
      } else {
        nextConfiguration[key] = value
      }

      return {
        ...prev,
        configuration: nextConfiguration,
      }
    })
  }

  const getActionIcon = (actionType: string) => {
    const IconComponent = ACTION_ICONS[actionType as keyof typeof ACTION_ICONS] || Settings
    return <IconComponent className="h-5 w-5" />
  }

  const getActionTypeInfo = (actionType: string) => {
    if (!Array.isArray(safeActionTypes) || safeActionTypes.length === 0) {
      return { value: actionType, label: actionType, icon: '', description: '' }
    }
    return safeActionTypes.find(type => type.value === actionType) || safeActionTypes[0] || { value: actionType, label: actionType, icon: '', description: '' }
  }

  const getConfigurationFields = (actionType: string): ConfigField[] => {
    switch (actionType) {
      case 'email':
      case 'send_email':
        return getEmailConfigFields(emailTemplates)
      case 'calendar':
      case 'schedule_meeting':
        return MEETING_CONFIG_FIELDS // Empty - configured on offer
      default:
        return []
    }
  }

  const renderConfigurationField = (field: ConfigField) => {
    const storedValue = actionForm.configuration[field.key]
    const value =
      storedValue !== undefined && storedValue !== null
        ? storedValue
        : field.type === 'checkbox'
          ? false
          : ''

    let inputElement: React.ReactNode = null

    switch (field.type) {
      case 'text':
        inputElement = (
          <Input
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => updateConfiguration(field.key, e.target.value)}
            placeholder={field.placeholder || `Enter ${(field.label && field.label.toLowerCase()) || ''}`}
            required={field.required}
          />
        )
        break
      case 'number':
        inputElement = (
          <Input
            type="number"
            value={value === undefined || value === null ? '' : value}
            onChange={(e) =>
              updateConfiguration(
                field.key,
                e.target.value === '' ? undefined : Number(e.target.value)
              )
            }
            placeholder={field.placeholder || `Enter ${(field.label && field.label.toLowerCase()) || ''}`}
            required={field.required}
            min={field.min ?? 0}
          />
        )
        break
      case 'textarea':
        inputElement = (
          <Textarea
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => updateConfiguration(field.key, e.target.value)}
            placeholder={field.placeholder || `Enter ${(field.label && field.label.toLowerCase()) || ''}`}
            required={field.required}
            rows={3}
          />
        )
        break
      case 'select':
        if (field.key === EMAIL_TEMPLATE_FIELD_KEY && field.templateOptions) {
          inputElement = (
            <Select
              value={value ? String(value) : ''}
              onChange={(e) =>
                updateConfiguration(field.key, e.target.value ? parseInt(e.target.value) : undefined)
              }
              required={field.required}
              options={[
                { value: '', label: t('hiringProcess.selectEmailTemplate') },
                ...field.templateOptions.map((template: EmailTemplate) => ({
                  value: String(template.id),
                  label: `${getEmailTemplateDisplayName(template, displayLanguage, t)} (${template.template_type})`
                }))
              ]}
              placeholder={t('hiringProcess.selectEmailTemplate')}
            />
          )
        } else {
          const options = Array.isArray(field.options) ? field.options : []
          inputElement = (
            <Select
              value={value ? String(value) : ''}
              onChange={(e) => updateConfiguration(field.key, e.target.value || undefined)}
              required={field.required}
              options={options.map((option: string) => ({
                value: option,
                label: option.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l && l.toUpperCase())
              }))}
              placeholder={field.placeholder || `Select ${field.label}`}
            />
          )
        }
        break
      case 'checkbox':
        // For calendar actions, render checkbox without label (label is shown separately)
        const isCalendarCheckbox = field.key === 'show_schedule_button'
        inputElement = (
          <label className={`flex items-center ${isCalendarCheckbox ? 'cursor-pointer' : ''}`}>
            <input
              type="checkbox"
              checked={Boolean(value)}
              onChange={(e) => updateConfiguration(field.key, e.target.checked)}
              className="h-5 w-5 rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-500 focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 cursor-pointer"
            />
            {!isCalendarCheckbox && (
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{field.label}</span>
            )}
          </label>
        )
        break
      default:
        inputElement = null
    }

    return (
      <>
        {inputElement}
        {field.helper && (
          <p className="mt-1 text-xs text-muted-foreground">
            {field.helper}
          </p>
        )}
      </>
    )
  }

  const currentConfigurationFields = (() => {
    const fields = getConfigurationFields(actionForm.action_type)
    return Array.isArray(fields) ? fields : []
  })()
  const selectedActionType = getActionTypeInfo(actionForm.action_type)

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={action ? t('hiringProcess.editAction') : t('hiringProcess.createAction')}
      className="max-w-4xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Action Type Selection */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <label className="block text-sm font-medium text-blue-900 dark:text-blue-100 mb-3">
            {t('hiringProcess.actionType')}
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {(() => {
              const arr = Array.isArray(safeActionTypes) ? safeActionTypes : []
              return arr.map((actionType) => (
              <button
                key={actionType.value}
                type="button"
                onClick={() => {
                  const isCalendarAction = actionType.value === 'calendar' || actionType.value === 'schedule_meeting'
                  setActionForm({ 
                    ...actionForm, 
                    action_type: actionType.value, 
                    configuration: {},
                    // Set default trigger type for calendar actions (hidden but needed for backend)
                    trigger_type: isCalendarAction ? 'manual' : actionForm.trigger_type
                  })
                }}
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  actionForm.action_type === actionType.value
                    ? 'border-blue-500 dark:border-blue-400 bg-blue-100 dark:bg-blue-900/40'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">{actionType.icon}</span>
                  <span className="font-medium text-sm text-foreground">{actionType.label}</span>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400">{actionType.description}</p>
              </button>
              ))
            })()}
          </div>
        </div>

        {/* Schedule Meeting Button Option - Show inline for calendar actions */}
        {(actionForm.action_type === 'calendar' || actionForm.action_type === 'schedule_meeting') && 
         currentConfigurationFields.length > 0 && (
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex-1 pr-4">
              <label className="text-sm font-medium text-gray-900 dark:text-gray-100 block">
                Show Schedule Meeting button
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Display the button on candidate cards in the kanban board
              </p>
            </div>
            {currentConfigurationFields.map((field) => {
              const storedValue = actionForm.configuration[field.key]
              const value = storedValue !== undefined && storedValue !== null ? storedValue : false
              return (
                <div key={field.key} className="flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={Boolean(value)}
                    onChange={(e) => updateConfiguration(field.key, e.target.checked)}
                    className="h-5 w-5 rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-500 focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 cursor-pointer"
                  />
                </div>
              )
            })}
          </div>
        )}

        {/* Trigger Type - Hidden for calendar actions */}
        {actionForm.action_type !== 'calendar' && actionForm.action_type !== 'schedule_meeting' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('hiringProcess.triggerType')} *
              </label>
              <Select
                value={actionForm.trigger_type}
                onChange={(e) => setActionForm({ ...actionForm, trigger_type: e.target.value })}
                options={(() => {
                  const filteredTypes = getFilteredTriggerTypes()
                  return filteredTypes.map((triggerType) => ({
                    value: triggerType.value,
                    label: triggerType.label || t(`hiringProcess.triggerTypes.${triggerType.value}`) || triggerType.value
                  }))
                })()}
                placeholder={t('hiringProcess.selectTriggerType') || 'Select trigger type'}
                required
              />
            </div>
          </div>
        )}

        {/* Action Configuration - For non-calendar actions */}
        {currentConfigurationFields.length > 0 && 
         actionForm.action_type !== 'calendar' && 
         actionForm.action_type !== 'schedule_meeting' && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              {selectedActionType.label} {t('hiringProcess.configuration')}
            </h3>
            
            {loadingTemplates && actionForm.action_type === 'email' ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="ml-3 text-sm text-muted-foreground">
                  {t('hiringProcess.loadingEmailTemplates')}
                </span>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {currentConfigurationFields.map((field) => (
                  <div key={field.key} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    {renderConfigurationField(field)}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit">
            {action ? t('common.update') : t('common.create')}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
